import { z } from "@hono/zod-openapi";
import { breakOverdueV1 } from "../../hoursOfService/breakOverdue";
import { cycleLimitExceededV1 } from "../../hoursOfService/cycleLimitExceeded";
import { drivingLimitExceededV1 } from "../../hoursOfService/drivingLimitExceeded";
import { dutyWindowExceededV1 } from "../../hoursOfService/dutyWindowExceeded";
import type { TmsEventBodyOf, TmsEventKind } from "../../taxonomy";

/**
 * A driver's hours-of-service clock running out.
 *
 * The vendor reports clocks as periodic snapshots with violation intervals attached; a violation's start is
 * vendor-stated and stable across snapshots, which is what makes it an anchor rather than a recomputed guess.
 *
 * The kinds it may state are declared here once, and the registry holds the subject to them.
 * `satisfies` rather than an annotation, so each name stays a literal and a typo is still a compile error.
 */
export const hosClockViolationKinds = [
  "breakOverdue",
  "cycleLimitExceeded",
  "drivingLimitExceeded",
  "dutyWindowExceeded",
] as const satisfies readonly TmsEventKind[];

export type HosClockViolationKind = (typeof hosClockViolationKinds)[number];

/**
 * The type of `tms_event.hos_clock_violation.facts`: any of this subject's kinds, at any version it declares.
 *
 * What the generated Drizzle column's `.$type<…>()` names, and what the repository parses every written and read
 * value against — so the column's compile-time type and its runtime contents answer to the same schema.
 *
 * Written out one arm at a time rather than derived from the kind list above. Every version a column may hold
 * is named here, so shipping a second version of a kind is a visible edit in this file rather than a silent
 * widening, and forgetting it fails the first time the writer emits that version. When a kind does reach two,
 * its arms nest in a union on `version`: zod keeps only the first arm when a discriminator value repeats, so
 * listing both flat would quietly parse one version's documents against the other's schema.
 */
export const HosClockViolationFactsSchema = z.discriminatedUnion("kind", [
  breakOverdueV1.facts,
  cycleLimitExceededV1.facts,
  drivingLimitExceededV1.facts,
  dutyWindowExceededV1.facts,
]);

export type HosClockViolationFacts = z.infer<typeof HosClockViolationFactsSchema>;

/**
 * What a HosClockViolation normalizer produces: references and facts, without the base the writer supplies.
 */
export type HosClockViolationBody = TmsEventBodyOf<HosClockViolationKind>;
